import React, { useEffect, useRef, useState } from "react";
import ZoomVideo, {
  WhiteboardSharePermissionCode,
  WhiteboardInitiatePermissionCode,
} from "@zoom/videosdk";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../utils/firebase.utils";
import {
  ZOOM_CONFIG,
  ROLE_HOST,
  ROLE_PARTICIPANT,
  SESSION_PARAM,
  generateSessionName,
  buildMeetingLink,
  buildZoomSignature,
} from "./zoomShared";

// The "import pdf.worker.entry" pattern doesn't reliably resolve to a usable
// worker URL under this project's webpack 5 build, which silently breaks
// PDF loading. Serving pdfjs-dist's worker build as a plain static file
// from public/ (see public/pdf.worker.min.js, copied from
// node_modules/pdfjs-dist/build/pdf.worker.min.js — must match the
// installed pdfjs-dist version) sidesteps that bundling problem entirely.
GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// Zoom Video SDK's whiteboard has no API to insert an image/PDF into the
// board itself. So a PDF "handout" here is rendered client-side page-by-page with pdfjs and
// shown as an overlay drawn on top of the board, rather than actually living
// inside the whiteboard's own synced canvas.
//
// The rendered page image itself is uploaded to Firebase Storage and only
// the resulting download URL is sent over Zoom's command channel — the
// command channel's actual limits (per Zoom's docs: 512 characters per
// message, 2 messages/second per session, silently dropped above that) are
// nowhere near enough to carry image bytes for even one page.
const PDF_STORAGE_PATH = "whiteboard-pdf-handouts";

// Zoom Video SDK's whiteboard (client.getWhiteboardClient()) is only
// reachable through an active session — unlike Netless/Fastboard, there's
// no bare iframe URL to embed. So this page still joins a (audio/video-off)
// Zoom session in the background purely to unlock the whiteboard, then
// gets out of the way and shows just the board.

export default function ZoomWhiteboard() {
  const [sdkKey, setSdkKey] = useState(ZOOM_CONFIG.sdkKey);
  const [sdkSecret, setSdkSecret] = useState(ZOOM_CONFIG.sdkSecret);
  const [sessionName, setSessionName] = useState("");
  const [userName, setUserName] = useState("Guest");
  const [isHost, setIsHost] = useState(false);
  const [joinedViaLink, setJoinedViaLink] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  // 'off' | 'presenting' (I started it) | 'viewing' (someone else did)
  const [whiteboardState, setWhiteboardState] = useState("off");

  // Master-uploaded PDF handout, rendered page-by-page and broadcast to
  // everyone in the session (see PDF_STORAGE_PATH comment above).
  const [pdfFileName, setPdfFileName] = useState("");
  const [pdfPageNum, setPdfPageNum] = useState(0);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfImageUrl, setPdfImageUrl] = useState("");
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const clientRef = useRef(null);
  const whiteboardClientRef = useRef(null);
  const boardContainerRef = useRef(null);
  const whiteboardBusyRef = useRef(false);
  const pdfDocRef = useRef(null);
  // Latest rendered page, kept outside React state so the command-channel
  // listener (registered once at join time) can answer a late joiner's
  // sync request without closing over a stale render.
  const pdfCurrentRef = useRef({ url: "", page: 0, total: 0 });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get(SESSION_PARAM);
    if (sess) {
      setSessionName(sess);
      setIsHost(false);
      setJoinedViaLink(true);
    }
  }, []);

  // The whiteboard's first-run "welcome" dialog ships inside Zoom's
  // externally-hosted WhiteboardSdk bundle (loaded from their CDN at
  // runtime, not something this package exposes an API to configure), and
  // it only closes on a click of its own X or the Escape key. Fire that
  // same keystroke right after a viewer's board mounts so it dismisses
  // itself before they see it. Two attempts since the dialog can render a
  // beat after startWhiteboardView's promise resolves.
  const dismissWhiteboardIntroPopup = () => {
    const pressEscape = () => {
      document.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Escape", code: "Escape", keyCode: 27, which: 27, bubbles: true }),
      );
    };
    pressEscape();
    setTimeout(pressEscape, 600);
  };

  const startPresenting = async () => {
    if (whiteboardBusyRef.current) return;
    const wb = whiteboardClientRef.current;
    if (!wb || !boardContainerRef.current) return;
    if (!wb.canStartWhiteboard()) {
      setError("This session doesn't allow you to start a whiteboard right now.");
      return;
    }
    whiteboardBusyRef.current = true;
    try {
      await wb.startWhiteboardScreen(boardContainerRef.current);
      setWhiteboardState("presenting");
    } finally {
      whiteboardBusyRef.current = false;
    }
  };

  const renderPdfPageAssets = async (pdf, pageNum) => {
    const page = await pdf.getPage(pageNum);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(1600 / base.width, 2);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    // dataUrl gives the host instant local feedback without waiting on the
    // upload below; blob is what actually gets uploaded for participants.
    const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.6));
    return { dataUrl, blob };
  };

  const uploadAndBroadcastPdfPage = async (blob, pageNum, totalPages) => {
    const cmd = clientRef.current?.getCommandClient();
    if (!cmd || !blob) {
      console.log("[ZoomWhiteboard] pdf upload skipped", { hasCommandClient: !!cmd, hasBlob: !!blob });
      return;
    }
    const path = `${PDF_STORAGE_PATH}/${sessionName}/${pageNum}-${Date.now()}.jpg`;
    console.log("[ZoomWhiteboard] pdf upload starting", { path, pageNum, totalPages, blobSize: blob.size, blobType: blob.type });
    try {
      const fileRef = storageRef(storage, path);
      const uploadResult = await uploadBytes(fileRef, blob, { contentType: "image/jpeg" });
      console.log("[ZoomWhiteboard] pdf upload succeeded", uploadResult);
      const url = await getDownloadURL(fileRef);
      console.log("[ZoomWhiteboard] pdf download URL", url);
      pdfCurrentRef.current = { url, page: pageNum, total: totalPages };
      const sendResult = await cmd.send(JSON.stringify({ t: "pdf-page", page: pageNum, total: totalPages, url }));
      console.log("[ZoomWhiteboard] pdf page broadcast sent", { page: pageNum, total: totalPages, sendResult });
    } catch (e) {
      console.error("[ZoomWhiteboard] pdf upload/broadcast failed", { path, code: e?.code, message: e?.message, error: e });
    }
  };

  const showPdfPage = async (pageNum) => {
    const pdf = pdfDocRef.current;
    if (!pdf || pdfLoading) return;
    const clamped = Math.min(Math.max(pageNum, 1), pdf.numPages);
    setPdfLoading(true);
    console.log("[ZoomWhiteboard] rendering pdf page", { page: clamped, total: pdf.numPages });
    try {
      const { dataUrl, blob } = await renderPdfPageAssets(pdf, clamped);
      console.log("[ZoomWhiteboard] pdf page rendered", { page: clamped, dataUrlLength: dataUrl.length, blobSize: blob?.size });
      setPdfImageUrl(dataUrl);
      setPdfPageNum(clamped);
      setPdfVisible(true);
      await uploadAndBroadcastPdfPage(blob, clamped, pdf.numPages);
    } catch (e) {
      console.error("[ZoomWhiteboard] showPdfPage failed", { page: clamped, error: e });
      setError(e?.message || String(e));
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPdfLoading(true);
    console.log("[ZoomWhiteboard] pdf file selected", { name: file.name, size: file.size, type: file.type });
    try {
      const buf = await file.arrayBuffer();
      const pdf = await getDocument({ data: buf }).promise;
      console.log("[ZoomWhiteboard] pdf document loaded", { name: file.name, numPages: pdf.numPages });
      pdfDocRef.current = pdf;
      setPdfFileName(file.name);
      setPdfPageCount(pdf.numPages);
      await showPdfPage(1);
    } catch (e) {
      console.error("[ZoomWhiteboard] pdf load failed", { name: file.name, error: e });
      setError(e?.message || String(e));
      setPdfLoading(false);
    }
  };

  const togglePdfVisible = async () => {
    const next = !pdfVisible;
    setPdfVisible(next);
    try {
      await clientRef.current?.getCommandClient()?.send(JSON.stringify({ t: "pdf-visible", v: next }));
    } catch (e) {
      console.warn("[ZoomWhiteboard] pdf visibility broadcast failed", e);
    }
  };

  const closePdf = async () => {
    pdfDocRef.current = null;
    pdfCurrentRef.current = { url: "", page: 0, total: 0 };
    setPdfFileName("");
    setPdfImageUrl("");
    setPdfPageCount(0);
    setPdfPageNum(0);
    setPdfVisible(false);
    try {
      await clientRef.current?.getCommandClient()?.send(JSON.stringify({ t: "pdf-close" }));
    } catch (e) {
      console.warn("[ZoomWhiteboard] pdf close broadcast failed", e);
    }
  };

  const resetPdfState = () => {
    pdfDocRef.current = null;
    pdfCurrentRef.current = { url: "", page: 0, total: 0 };
    setPdfFileName("");
    setPdfImageUrl("");
    setPdfPageCount(0);
    setPdfPageNum(0);
    setPdfVisible(false);
    setPdfLoading(false);
  };

  const handleJoin = async ({ hostFlag, sessionNameOverride } = {}) => {
    const effectiveHost = hostFlag ?? isHost;
    const effectiveSession = (sessionNameOverride ?? sessionName).trim();

    if (!effectiveSession) {
      setError("Enter or generate a session name first.");
      return;
    }

    setError("");
    setConnecting(true);
    try {
      const signature = await buildZoomSignature({
        sdkKey,
        sdkSecret,
        sessionName: effectiveSession,
        role: effectiveHost ? ROLE_HOST : ROLE_PARTICIPANT,
      });

      const client = ZoomVideo.createClient();
      clientRef.current = client;
      await client.init("en-US", "Global", { patchJsMedia: true });

      client.on("peer-whiteboard-state-change", async (payload) => {
        // Zoom broadcasts this event to the presenter too — without this
        // check the host's own client would race startWhiteboardView()
        // against its own startWhiteboardScreen() on the same container.
        const isMe = payload.userId === client.getCurrentUserInfo()?.userId;
        const wb = whiteboardClientRef.current;
        if (isMe || !wb || !boardContainerRef.current) return;
        try {
          if (payload.action === "Start") {
            await wb.startWhiteboardView(boardContainerRef.current, payload.userId);
            setWhiteboardState("viewing");
            dismissWhiteboardIntroPopup();
          } else if (payload.action === "Stop") {
            await wb.stopWhiteboardView();
            setWhiteboardState("off");
          }
        } catch (e) {
          console.warn("[ZoomWhiteboard] whiteboard view failed", e);
        }
      });

      client.on("command-channel-message", (payload) => {
        let msg;
        try {
          msg = JSON.parse(payload.text);
        } catch {
          return; // not one of ours
        }
        console.log("[ZoomWhiteboard] command-channel message received", msg);
        switch (msg.t) {
          case "pdf-page":
            pdfCurrentRef.current = { url: msg.url, page: msg.page, total: msg.total };
            setPdfImageUrl(msg.url);
            setPdfPageNum(msg.page);
            setPdfPageCount(msg.total);
            setPdfVisible(true);
            console.log("[ZoomWhiteboard] pdf page applied from broadcast", { page: msg.page, total: msg.total, url: msg.url });
            break;
          case "pdf-visible":
            setPdfVisible(!!msg.v);
            break;
          case "pdf-close":
            setPdfImageUrl("");
            setPdfPageCount(0);
            setPdfPageNum(0);
            setPdfVisible(false);
            break;
          case "pdf-sync-request":
            if (effectiveHost && pdfCurrentRef.current.url) {
              const { url, page, total } = pdfCurrentRef.current;
              console.log("[ZoomWhiteboard] resending current pdf page for sync request", { page, total, url });
              clientRef.current
                ?.getCommandClient()
                ?.send(JSON.stringify({ t: "pdf-page", page, total, url }))
                .catch((e) => console.error("[ZoomWhiteboard] pdf sync response failed", e));
            } else {
              console.log("[ZoomWhiteboard] sync request ignored", { effectiveHost, hasUrl: !!pdfCurrentRef.current.url });
            }
            break;
          default:
            break;
        }
      });

      await client.join(effectiveSession, signature, userName);

      whiteboardClientRef.current = client.getWhiteboardClient();

      // Lock the whiteboard to the host so participants can only ever view
      // it — they can't start their own board or grab drawing control away
      // from the host's active one.
      if (effectiveHost) {
        try {
          await whiteboardClientRef.current.setWhiteboardPermission({
            initiatePermission: WhiteboardInitiatePermissionCode.HostOnly,
            sharePermission: WhiteboardSharePermissionCode.LockShare,
          });
        } catch (e) {
          console.warn("[ZoomWhiteboard] lock whiteboard permission failed", e);
        }
      }

      setIsHost(effectiveHost);
      setSessionName(effectiveSession);
      if (effectiveHost) setMeetingLink(buildMeetingLink(effectiveSession));

      setJoined(true);
      // Starting/catching up the board happens in a useEffect keyed on
      // `joined` — boardContainerRef isn't mounted yet at this point since
      // React hasn't committed the setJoined(true) re-render (it only
      // conditionally renders the container div once joined is true).
    } catch (err) {
      console.error("[ZoomWhiteboard] join failed", err);
      setError(err?.message || String(err));
    } finally {
      setConnecting(false);
    }
  };

  const handleCreateMeeting = () => {
    const finalSession = sessionName.trim() || generateSessionName();
    handleJoin({ hostFlag: true, sessionNameOverride: finalSession });
  };

  const handleJoinMeeting = () => {
    if (!sessionName.trim()) {
      setError("Enter a session name, or open a meeting link shared by the master.");
      return;
    }
    handleJoin({ hostFlag: false, sessionNameOverride: sessionName });
  };

  const handleLeave = async () => {
    const client = clientRef.current;
    try {
      const wb = whiteboardClientRef.current;
      if (wb) {
        if (whiteboardState === "presenting") await wb.stopWhiteboardScreen();
        else if (whiteboardState === "viewing") await wb.stopWhiteboardView();
      }
      if (client) await client.leave();
    } catch (e) {
      console.warn("[ZoomWhiteboard] leave error", e);
    } finally {
      clientRef.current = null;
      whiteboardClientRef.current = null;
      setJoined(false);
      setWhiteboardState("off");
      setMeetingLink("");
      resetPdfState();
    }
  };

  useEffect(() => {
    return () => {
      if (clientRef.current) handleLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host: open a blank board once boardContainerRef is actually mounted
  // (right after setJoined(true) in handleJoin is too early — React hasn't
  // committed that render yet). Participant: catch up if a presenter is
  // already mid-session (e.g. they joined a bit late and missed the
  // "Start" event, which only fires for changes after you're subscribed).
  useEffect(() => {
    if (!joined) return;
    if (isHost) {
      startPresenting();
      return;
    }
    // Ask the host to resend whatever PDF page is currently up, in case one
    // was already showing before this participant joined.
    clientRef.current
      ?.getCommandClient()
      ?.send(JSON.stringify({ t: "pdf-sync-request" }))
      .catch((e) => console.warn("[ZoomWhiteboard] pdf sync request failed", e));

    const wb = whiteboardClientRef.current;
    const container = boardContainerRef.current;
    if (!wb || !container) return;
    const presenter = wb.getWhiteboardPresenter?.();
    if (!presenter) return;
    wb.startWhiteboardView(container, presenter.userId)
      .then(() => {
        setWhiteboardState("viewing");
        dismissWhiteboardIntroPopup();
      })
      .catch((e) => console.warn("[ZoomWhiteboard] whiteboard catch-up failed", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, isHost]);

  const toggleWhiteboard = async () => {
    if (!isHost) return;
    const wb = whiteboardClientRef.current;
    if (!wb) return;
    try {
      if (whiteboardState === "presenting") {
        await wb.stopWhiteboardScreen();
        setWhiteboardState("off");
      } else if (whiteboardState === "off") {
        await startPresenting();
      }
    } catch (e) {
      console.warn("[ZoomWhiteboard] toggleWhiteboard failed", e);
      setError(e?.message || String(e));
    }
  };

  const exportPdf = async () => {
    const wb = whiteboardClientRef.current;
    if (!wb) return;
    try {
      await wb.exportWhiteboard("pdf", sessionName, false);
    } catch (e) {
      console.warn("[ZoomWhiteboard] export failed", e);
      setError(e?.message || String(e));
    }
  };

  const copyMeetingLink = () => {
    if (!meetingLink) return;
    navigator.clipboard
      .writeText(meetingLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((err) => console.error("Failed to copy meeting link", err));
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 4 }}>Zoom Whiteboard — Test Page</h2>
      <p style={{ color: "#555", marginBottom: 20 }}>
        Whiteboard-only sandbox, backed by Zoom Video SDK's native whiteboard.
      </p>

      {!joined && (
        <div style={styles.configBox}>
          <div style={styles.configRow}>
            <label style={styles.label}>SDK Key</label>
            <input style={styles.input} type="text" value={sdkKey} onChange={(e) => setSdkKey(e.target.value)} />
          </div>
          <div style={styles.configRow}>
            <label style={styles.label}>SDK Secret</label>
            <input style={styles.input} type="password" value={sdkSecret} onChange={(e) => setSdkSecret(e.target.value)} />
          </div>
          <div style={styles.configRow}>
            <label style={styles.label}>Session Name</label>
            <input
              style={styles.input}
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder={joinedViaLink ? "" : "Leave blank to auto-generate when creating"}
              readOnly={joinedViaLink}
            />
          </div>
          <div style={styles.configRow}>
            <label style={styles.label}>Display Name</label>
            <input style={styles.input} type="text" value={userName} onChange={(e) => setUserName(e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            {!joinedViaLink && (
              <button style={styles.btn} onClick={handleCreateMeeting} disabled={connecting}>
                {connecting ? "Starting..." : "Create Meeting"}
              </button>
            )}
            <button
              style={{ ...styles.btn, backgroundColor: "#374151" }}
              onClick={handleJoinMeeting}
              disabled={connecting}
            >
              {connecting ? "Joining..." : "Join Meeting"}
            </button>
          </div>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {joined && (
        <>
          <div style={styles.boardWrap}>
            <div ref={boardContainerRef} style={styles.board} />
            {/* Zoom's whiteboard sets pointer-events:auto on its own toolbar/canvas
                elements, overriding any pointer-events:none we set on the container
                itself, so a CSS block there alone doesn't stop drawing. This
                transparent pane sits above it and physically catches every
                click/drag/touch before the widget underneath receives them. */}
            {!isHost && <div style={styles.viewOnlyOverlay} />}
            {/* Stays mounted once a PDF is loaded so toggling pdfVisible animates
                as a drawer sliding up and away, instead of an instant show/hide. */}
            {pdfImageUrl && (
              <div
                style={{
                  ...styles.pdfOverlay,
                  transform: pdfVisible ? "translateY(0)" : "translateY(-100%)",
                  opacity: pdfVisible ? 1 : 0,
                  pointerEvents: pdfVisible ? "auto" : "none",
                }}
              >
                <img src={pdfImageUrl} alt={`PDF page ${pdfPageNum}`} style={styles.pdfImage} />
                {isHost && (
                  <div style={styles.pdfNavBar}>
                    <button
                      style={styles.navBtn}
                      onClick={() => showPdfPage(pdfPageNum - 1)}
                      disabled={pdfLoading || pdfPageNum <= 1}
                    >
                      Prev
                    </button>
                    <span style={styles.pdfPageLabel}>
                      {pdfLoading ? "Loading..." : `Page ${pdfPageNum} / ${pdfPageCount}`}
                    </span>
                    <button
                      style={styles.navBtn}
                      onClick={() => showPdfPage(pdfPageNum + 1)}
                      disabled={pdfLoading || pdfPageNum >= pdfPageCount}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <h3>Whiteboard Session Info</h3>
            <p><strong>Session:</strong> {sessionName}</p>
            <p><strong>Your role:</strong> {isHost ? "Master (host)" : "Participant"}</p>
            <p><strong>Status:</strong> {whiteboardState}</p>
            {!isHost && pdfPageCount > 0 && (
              <p>
                <strong>Handout:</strong> Page {pdfPageNum} / {pdfPageCount}
                {!pdfVisible && " (hidden by master)"}
              </p>
            )}

            {isHost && (
              <div style={styles.pdfControlBox}>
                <label style={styles.label}>PDF Handout</label>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                  <input type="file" accept="application/pdf" onChange={handlePdfFileChange} disabled={pdfLoading} />
                  {pdfFileName && <span style={{ fontSize: 13, color: "#374151" }}>{pdfFileName}</span>}
                </div>
                {pdfPageCount > 0 && (
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
                    <button
                      style={styles.copyBtn}
                      onClick={() => showPdfPage(pdfPageNum - 1)}
                      disabled={pdfLoading || pdfPageNum <= 1}
                    >
                      Prev
                    </button>
                    <span>{pdfLoading ? "Loading..." : `Page ${pdfPageNum} / ${pdfPageCount}`}</span>
                    <button
                      style={styles.copyBtn}
                      onClick={() => showPdfPage(pdfPageNum + 1)}
                      disabled={pdfLoading || pdfPageNum >= pdfPageCount}
                    >
                      Next
                    </button>
                    <button style={styles.copyBtn} onClick={togglePdfVisible}>
                      {pdfVisible ? "▴ Hide (reveal whiteboard)" : "▾ Show to participants"}
                    </button>
                    <button style={{ ...styles.copyBtn, backgroundColor: "#dc2626" }} onClick={closePdf}>
                      Close PDF
                    </button>
                  </div>
                )}
              </div>
            )}

            {isHost && meetingLink && (
              <div style={styles.linkBox}>
                <label style={styles.label}>Meeting link (share with customer)</label>
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <input style={styles.input} type="text" value={meetingLink} readOnly />
                  <button style={styles.copyBtn} onClick={copyMeetingLink}>
                    {linkCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              {isHost && (
                <button style={styles.btn} onClick={toggleWhiteboard}>
                  {whiteboardState === "presenting" ? "Stop Whiteboard" : "Start Whiteboard"}
                </button>
              )}
              {whiteboardState === "presenting" && (
                <button style={{ ...styles.btn, backgroundColor: "#374151" }} onClick={exportPdf}>
                  Export PDF
                </button>
              )}
              <button style={{ ...styles.btn, backgroundColor: "#dc2626" }} onClick={handleLeave}>
                Leave
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  configBox: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: "#f9fafb",
    maxWidth: 480,
  },
  configRow: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  input: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 14,
  },
  btn: {
    padding: "10px 16px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "#16a34a",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  error: {
    marginTop: 12,
    padding: "10px 14px",
    borderRadius: 6,
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    maxWidth: 640,
  },
  linkBox: {
    marginTop: 12,
    padding: 16,
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: "#f9fafb",
    maxWidth: 640,
  },
  copyBtn: {
    padding: "8px 16px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "#008CBA",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // position: relative + no border/padding/overflow-hidden directly on the
  // element passed to startWhiteboardScreen()/startWhiteboardView() — Zoom
  // anchors its toolbar overlay to this container.
  board: {
    position: "relative",
    height: "70vh",
    width: "100%",
    border: "2px solid #000",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  boardWrap: {
    position: "relative",
    // Clips the pdfOverlay drawer while it's translated up and out of view.
    overflow: "hidden",
    borderRadius: 8,
  },
  viewOnlyOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background: "transparent",
    cursor: "default",
    touchAction: "none",
  },
  pdfControlBox: {
    marginTop: 12,
    padding: 16,
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: "#f9fafb",
    maxWidth: 640,
  },
  // Sits above the whiteboard (and above viewOnlyOverlay) so the handout
  // fully covers the board while it's showing, on both host and viewer.
  // transform/opacity are set inline per-render (based on pdfVisible) so
  // toggling animates as a drawer sliding up and away, rather than an
  // instant show/hide — boardWrap below clips it while it's off-screen.
  pdfOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    backgroundColor: "#fff",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 320ms ease, opacity 320ms ease",
  },
  pdfImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  pdfNavBar: {
    position: "absolute",
    bottom: 12,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    borderRadius: 999,
    backgroundColor: "rgba(0,0,0,0.75)",
    color: "#fff",
  },
  navBtn: {
    padding: "4px 12px",
    borderRadius: 999,
    border: "none",
    backgroundColor: "#fff",
    color: "#111",
    fontWeight: 600,
    cursor: "pointer",
  },
  pdfPageLabel: {
    fontSize: 13,
    whiteSpace: "nowrap",
  },
};
